import {
  BaseQueryFn,
  createApi,
  EndpointBuilder,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  retry,
} from '@reduxjs/toolkit/query/react';

import { prepareHeaders } from '@/api/common';
import { configuration } from '@/config';

const reducerPath = 'keycloak.api' as const;

type BaseQuery = ReturnType<typeof fetchBaseQuery>;
type ReducerPath = 'keycloak-api';

const query = fetchBaseQuery({
  baseUrl: `${configuration.keycloakUrl}/realms/${configuration.keycloakRealm}`,
  prepareHeaders: prepareHeaders,
  credentials: 'include',
});

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  any,
  FetchBaseQueryMeta
> = async (args: string | FetchArgs, api: any, extraOptions: any): Promise<any> => {
  if (typeof args === 'string' && args === '') {
    return { data: undefined };
  } else if (typeof args === 'object' && args.url === '') {
    return { data: undefined };
  }

  return query(args, api, extraOptions);
};

interface DeleteTotpCredentialRequest {
  readonly code: string;
}

interface ResetPasswordRequest {
  readonly oldPassword: string;
  readonly newPassword: string;
}

export const keycloakApi = createApi({
  baseQuery: retry(customBaseQuery, {
    maxRetries: 0,
  }),
  endpoints: (build: EndpointBuilder<BaseQuery, never, ReducerPath>): any => {
    return {
      resetPassword: build.mutation<void, ResetPasswordRequest>({
        query: (request: ResetPasswordRequest): FetchArgs => ({
          url: '/utils/update-password',
          method: 'POST',
          body: request,
        }),
      }),
      deleteTotpCredential: build.mutation<void, DeleteTotpCredentialRequest>({
        query: (request: DeleteTotpCredentialRequest): FetchArgs => ({
          url: `/utils/totp?code=${encodeURIComponent(request.code)}`,
          method: 'DELETE',
        }),
      }),
    };
  },
  reducerPath: reducerPath,
});

export const { useDeleteTotpCredentialMutation, useResetPasswordMutation } = keycloakApi;
